import { useState } from 'react';

import type { IdeaDetail } from '@/api/types';
import { Pagination } from '@/ui/Pagination';
import { useTimeZone } from '@/ui/useTimeZone';

import { HistoryRow } from './HistoryRow';

/** The runs whose refinement is open; each opens under its row on its own. */
function useOpenSet() {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  return { open, toggle };
}

/** Paged in the browser like Users: the idea already carries every run. */
function usePagedRuns(idea: IdeaDetail) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pages = Math.max(1, Math.ceil(idea.history.length / pageSize));
  const onPage = Math.min(page, pages);
  const runs = idea.history.slice((onPage - 1) * pageSize, onPage * pageSize);
  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };
  return { runs, onPage, pageSize, setPage, changePageSize };
}

function TimeZoneToggle({ utc, setUtc }: { utc: boolean; setUtc: (utc: boolean) => void }) {
  return (
    <div className="toolbar history-toolbar">
      <div className="segmented" role="radiogroup" aria-label="Show times in">
        <button type="button" role="radio" aria-checked={!utc} onClick={() => setUtc(false)}>Local</button>
        <button type="button" role="radio" aria-checked={utc} onClick={() => setUtc(true)}>UTC</button>
      </div>
    </div>
  );
}

function HistoryHead({ utc }: { utc: boolean }) {
  return (
    <thead>
      <tr>
        <th scope="col">{utc ? 'Requested (UTC)' : 'Requested'}</th>
        <th scope="col">Status</th>
        <th scope="col" className="num" title="From the worker picking the run up to its end; time in the queue is not counted">
          Duration
        </th>
        <th scope="col" className="num">Total</th>
        <th scope="col">Verdict</th>
        <th scope="col">Model</th>
        <th scope="col">Prompt</th>
        <th scope="col">By</th>
        <th scope="col"><span className="sr-only">Open</span></th>
      </tr>
    </thead>
  );
}

export function HistoryTable({ idea, viewing, onView }: { idea: IdeaDetail; viewing: string | null; onView: (id: string | null) => void }) {
  const { open, toggle } = useOpenSet();
  const { runs, onPage, pageSize, setPage, changePageSize } = usePagedRuns(idea);
  // Local or UTC: the app-wide choice, shared with the audit log.
  const [utc, setUtc] = useTimeZone();
  const currentId = idea.current_research?.id;
  return (
    <>
      <TimeZoneToggle utc={utc} setUtc={setUtc} />
      <div className="table-scroll">
        <table className="data compact history-table">
          <HistoryHead utc={utc} />
          <tbody>
            {runs.map((run, index) => {
              const isCurrent = run.id === currentId;
              return (
                <HistoryRow
                  key={run.id}
                  run={run}
                  striped={index % 2 === 1}
                  isCurrent={isCurrent}
                  isViewing={viewing ? run.id === viewing : isCurrent}
                  shown={open.has(run.id)}
                  utc={utc}
                  onToggle={toggle}
                  onView={onView}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        page={onPage}
        pageSize={pageSize}
        total={idea.history.length}
        noun={idea.history.length === 1 ? 'run' : 'runs'}
        onPage={setPage}
        onPageSize={changePageSize}
      />
    </>
  );
}
